interface CacheFnOptions<Fn extends (...args: never[]) => unknown> {
	/**
	 * Time to live for cache entries, in milliseconds. if not specified, defaults to 1 min (60_000 ms).
	 */
	ttl?: number;
	/**
	 * Function to generate a cache key from the function arguments.
	 * By default, it uses JSON.stringify to create a key based on the arguments.
	 * @default JSON.stringify
	 */
	keyGenerator?: (args: Parameters<Fn>) => unknown;
	/**
	 * Maximum number of entries to keep in the cache.
	 * When the cache exceeds this limit, the least recently used entry will be removed.
	 * @default 100
	 */
	maxSize?: number;
	/**
	 * this function defaults to only caching on the client side.
	 * If set to true, it will also cache responses on the server side.
	 * @default false
	 */
	includeServer?: boolean;
}

class Cache {
	private readonly cache: Array<[unknown, unknown]> = [];
	private readonly maxSize: number;
	private readonly ttl: number | undefined;

	constructor(maxSize: number, ttl = 60_000) {
		this.maxSize = maxSize;
		this.ttl = ttl;
	}

	get(key: unknown): unknown {
		for (let i = this.cache.length - 1; i >= 0; i--) {
			if (this.cache[i][0] === key) {
				// Move the found entry to the end of the cache to mark it as recently used
				const entry = this.cache.splice(i, 1)[0];
				this.cache.push(entry);
				return entry[1]; // Return the cached value
			}
		}
	}

	set(key: unknown, value: unknown): void {
		if (this.cache.length >= this.maxSize) {
			// Remove the least recently used entry
			this.cache.shift();
		}
		this.cache.push([key, value]);
		if (this.ttl) {
			setTimeout(() => {
				this.delete(key);
			}, this.ttl);
		}
	}

	delete(key: unknown): void {
		for (let i = 0; i < this.cache.length; i++) {
			if (this.cache[i][0] === key) {
				this.cache.splice(i, 1);
				return; // Exit after deleting the entry
			}
		}
	}

	has(key: unknown): boolean {
		for (let i = this.cache.length - 1; i >= 0; i--) {
			if (this.cache[i][0] === key) {
				return true; // Key found in cache
			}
		}
		return false; // Key not found in cache
	}
}

export function cacheFn<Fn extends (...args: never[]) => unknown>(fn: Fn, options: CacheFnOptions<Fn> = {}) {
	const { ttl, keyGenerator = JSON.stringify, maxSize = 100 } = options;
	const cache = new Cache(maxSize, ttl);
	return (...args: Parameters<Fn>): ReturnType<Fn> => {
		const key = keyGenerator(args);
		if (cache.has(key)) {
			return cache.get(key) as ReturnType<Fn>;
		}
		const result = fn(...args) as ReturnType<Fn>;
		cache.set(key, result);
		return result;
	};
}
